import store from 'a/store';
import React, { useState, useEffect } from 'react';
import { getLocalization } from 'root/activities/selectors/languageSelectors';
import jsonStableStringify from 'fast-json-stable-stringify';
import Env from './Env';
import { PROMISE_STATUSES } from './constants';
import { promiseAllSettled, aquaAxios, makeUrlPath } from './utilityFunctions';
import { getValidatedCountryCode } from './StaticAssetsManifest';

export const getUrlFromAssetObj = ({ url, path, file, countryCode } = {}) => {
  if (url) {
    return url;
  }
  if (path && file) {
    return makeUrlPath(
      Env.GAME_CONTENT_URL,
      path,
      getValidatedCountryCode({ path, countryCode, file }),
      file,
    );
  }
  return '';
};

const assetCache = new Map();
const componentCache = new Map();
const cacheKeyObjs = new Map();

const getCacheKeyObj = (keyStr) => {
  let keyObj = cacheKeyObjs.get(keyStr);
  if (!keyObj) {
    keyObj = { key: keyStr };
    cacheKeyObjs.set(keyStr, keyObj);
  }
  return keyObj;
};

const assetCacheSetter = (url, value, status = PROMISE_STATUSES.FULFILLED) => {
  if (!url || !value) {
    return;
  }
  assetCache.set(getCacheKeyObj(url), {
    value,
    status,
  });
};

const assetCacheGetter = (url, isIncludeAllStatues = false) => {
  const { status, value } = assetCache.get(getCacheKeyObj(url)) || {};
  return (isIncludeAllStatues || status === PROMISE_STATUSES.FULFILLED) && value;
};

const fetchAsset = async (url) => {
  const cachedAsset = assetCacheGetter(url, true);
  if (cachedAsset) {
    return cachedAsset;
  }

  const svgFetchPromise = aquaAxios({
    url,
    responseType: 'text',
  }).then(({ data }) => {
    assetCacheSetter(url, data);
    return data;
  });

  assetCacheSetter(url, svgFetchPromise);
  return svgFetchPromise;
};

export const loadAsset = assetObj => fetchAsset(getUrlFromAssetObj(assetObj));

export const loadAssets = async (assetObjs = []) => promiseAllSettled(assetObjs.map(loadAsset));

export const useLoadAsset = (assetObj, { ownAsset } = {}) => {
  const isSkipFetch = !!ownAsset;
  const url = getUrlFromAssetObj(assetObj);
  const [asset, setAsset] = useState(assetCacheGetter(url) || null);
  useEffect(() => {
    if (!isSkipFetch) {
      fetchAsset(url).then(setAsset);
    }
  }, [isSkipFetch, url, setAsset]);
  return ownAsset || asset;
};

export const getLoadedComponent = (assetObj, Component = () => null) => {
  const countryCode = getLocalization(store.getState());
  const componentKey = getCacheKeyObj(jsonStableStringify(assetObj));
  const LoadedComponent = componentCache.get(componentKey);
  if (LoadedComponent) {
    return LoadedComponent;
  }

  const asset = assetCacheGetter(getUrlFromAssetObj({ ...assetObj, countryCode }));
  const NewLoadedComponent = React.forwardRef(
    (props, ref) => <Component {...props} asset={asset} ref={ref} />,
  );
  componentCache.set(componentKey, NewLoadedComponent);
  return NewLoadedComponent;
};

export default {};
